import React, { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { Card, CardBody, CardHeader, Button, Input, Label, FormGroup, Spinner, ListGroup, ListGroupItem,
  Modal, ModalHeader, ModalBody, ModalFooter
 } from 'reactstrap';
import { ToastContainer, toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom'; // Importa o hook para ler a URL

// Imports do Leaflet
import { MapContainer, ImageOverlay, SVGOverlay, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-ant-path';
import { QRCodeSVG } from 'qrcode.react'; // Importa o componente do QR Code

import { fetchComAuth } from '../../common/api';
import { api } from "../../config";
import { findShortestPathWithContext, generateDirections } from '../../funcoes/roteamento';

const InteractiveStand = ({ stand, onClick }) => {
  const pathRef = useRef(null);
  const [labelPosition, setLabelPosition] = useState(null);

  // Usamos useEffect para configurar o observador
  useEffect(() => {
    // A verificação `pathRef.current` é crucial
    if (!pathRef.current) return;

    // Criamos um observador que nos notificará quando o elemento tiver um tamanho
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        // Quando o elemento for renderizado e tiver um tamanho real...
        const bbox = entry.target.getBBox();
        if (bbox.width > 0 || bbox.height > 0) {
          setLabelPosition({
            x: bbox.x + bbox.width / 2,
            y: bbox.y + bbox.height / 2,
          });
          // Importante: uma vez que medimos, paramos de observar para evitar cálculos desnecessários
          resizeObserver.unobserve(entry.target);
        }
      }
    });

    // Inicia a observação do nosso elemento <path>
    resizeObserver.observe(pathRef.current);

    // Função de limpeza: para o observador quando o componente é desmontado
    return () => resizeObserver.disconnect();
  }, [stand.path]); // A dependência continua a mesma

  return (
    <g
      style={{ cursor: 'pointer' }}
      onClick={() => onClick(stand)}
      onMouseOver={(e) => e.currentTarget.style.filter = 'brightness(1.2)'}
      onMouseOut={(e) => e.currentTarget.style.filter = 'none'}
    >
      <path
        ref={pathRef}
        d={stand.path}
        fill={stand.cor}
        stroke="#333"
        strokeWidth="1"
      />
      {labelPosition && (
        <text
          x={labelPosition.x}
          y={labelPosition.y}
          fontSize="22"
          fontWeight="bold"
          fill="white"
          stroke="black"
          strokeWidth="0.5"
          textAnchor="middle"
          alignmentBaseline="middle"
          style={{ pointerEvents: 'none' }}
        >
          {stand.nome}
        </text>
      )}
    </g>
  );
};

function AntPath({ positions }) {
  const map = useMap();
  useEffect(() => {
    let antPath;
    if (positions && positions.length > 0) {
      antPath = L.polyline.antPath(positions, {
        delay: 400, dashArray: [10, 20], weight: 6,
        color: "#F00", pulseColor: "#FFF",
      });
      antPath.addTo(map);
      map.fitBounds(antPath.getBounds().pad(0.1));
    }
    return () => { if (antPath) map.removeLayer(antPath) };
  }, [positions, map]);
  return null;
}

function MapAutoFit({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      // 'padding: [0, 0]' remove qualquer margem interna
      // 'map.invalidateSize()' força o mapa a ocupar toda a largura da div pai
      map.fitBounds(bounds, { padding: [0, 0] });
      setTimeout(() => {
        map.invalidateSize();
      }, 200);
    }
  }, [bounds, map]);
  return null;
}

const VisualizacaoRotaCadastro = ({ feiraId }) => {
  // Estados de dados
  const [isLoading, setIsLoading] = useState(true);
  const [mapData, setMapData] = useState(null);
  const [fundoUrl, setFundoUrl] = useState('');
  const [estandesCadastrados, setEstandesCadastrados] = useState([]);
  const [tiposEspacoFeira, setTiposEspacoFeira] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  
  // Estados de UI e Rota
  const [origemId, setOrigemId] = useState('');
  const [destinoId, setDestinoId] = useState('');
  const [rotaCalculada, setRotaCalculada] = useState(null);
  const [instrucoes, setInstrucoes] = useState([]);

  const [modalCompartilharVisivel, setModalCompartilharVisivel] = useState(false);

  // Hook para ler os parâmetros da URL
  const [searchParams] = useSearchParams();

  const tiposEspacoMap = useMemo(() => {
    const map = new Map();
    (tiposEspacoFeira || []).forEach((t) => {
      const id = Number(t?.tipo_espaco_id ?? t?.id);
      if (Number.isFinite(id)) map.set(id, t);
    });
    return map;
  }, [tiposEspacoFeira]);

  const normalizarCirculacao = (v) => {
    const s = String(v || '').toLowerCase().trim();
    if (s === 'externo') return 'externo';
    if (s === 'transicao') return 'transicao';
    return 'interno';
  };

  const getNodeCirculacao = (node) => {
    if (!node) return null;

    // 1) se vier no próprio node, usa
    if (node.circulacao) {
      return normalizarCirculacao(node.circulacao);
    }

    // 2) tenta inferir pelo tipo de espaço (destinos)
    const tipoId = Number(node?.tipo_espaco_id);
    if (Number.isFinite(tipoId)) {
      const tipo = tiposEspacoMap.get(tipoId);
      const circulacaoTipo = tipo?.circulacao || tipo?.circulacao_tipo; // ajuste conforme teu backend
      if (circulacaoTipo) return normalizarCirculacao(circulacaoTipo);
    }

    return null;
  };

  const getNodePermiteCirculacao = useCallback((node) => {
    if (!node) return false;

    if (node.type === 'intersection') return true;

    if (typeof node.permite_circulacao === 'boolean') {
      return node.permite_circulacao;
    }

    const tipoId = Number(node?.tipo_espaco_id);
    if (!Number.isFinite(tipoId)) return false;

    const tipo = tiposEspacoMap.get(tipoId);
    const valor = tipo?.permite_circulacao;

    if (typeof valor === 'boolean') return valor;

    const raw = String(valor ?? '').trim().toLowerCase();
    return raw === '1' || raw === 'true' || raw === 'sim';
  }, [tiposEspacoMap]);

  const calcularRotaContextual = (origem, destino) => {
    if (!origem || !destino) return null;

    const resultado = findShortestPathWithContext(nodes, edges, origem, destino, {
        mode: 'auto',
        allowFallback: true,
        getNodeCirculacao,
        getNodePermiteCirculacao, // <-- novo
        destinationBlockPenalty: 10000, // opcional
      }
     );

    if (resultado?.meta?.strategy === 'auto_fallback_flexivel') {
      toast.info('Não foi possível manter rota totalmente interna. Exibindo a melhor rota disponível.');
    }

    return resultado;
  };

  useEffect(() => {
    const carregarDadosCompletos = async () => {
        if (!feiraId) return;
        setIsLoading(true);
        try {
            const [mapaRes, estandesRes, tiposEspacoRes, grafoRes] = await Promise.all([
                fetchComAuth(`${api.API_URL}/rota/mapa/${feiraId}?refresh=1`),
                fetchComAuth(`${api.API_URL}/rota/estandes/${feiraId}?refresh=1`),
                fetchComAuth(`${api.API_URL}/feira/tiposEspaco/${feiraId}`),
                fetchComAuth(`${api.API_URL}/rota/caminhos/${feiraId}?refresh=1`)
            ]);

            const dataMapa = await mapaRes.json();
            if (dataMapa.status && dataMapa.data?.mapa?.mapa) {
                setMapData(dataMapa.data.mapa.mapa);
                setFundoUrl(dataMapa.data.fundoURL || "");
            }

            const dataEstandes = await estandesRes.json();
            
            setEstandesCadastrados(dataEstandes.data || []);

            const dataTiposEspaco = await tiposEspacoRes.json();
            setTiposEspacoFeira(dataTiposEspaco.data || []);

            const dataGrafo = await grafoRes.json();
            if (dataGrafo.status && dataGrafo.data) {
              const nodesAdaptados = dataGrafo.data.nodes.map(nodeDB => ({
                id: nodeDB.id,
                x: nodeDB.posicao_x,
                y: nodeDB.posicao_y,
                type: nodeDB.tipo,
                name: nodeDB.nome || nodeDB.descricao,
                description: nodeDB.descricao,
                tipo_espaco_id: nodeDB.tipo_espaco_id ?? null,
                circulacao: normalizarCirculacao(nodeDB.circulacao), // novo
              }));
              
              const edgesAdaptados = (dataGrafo.data.edges || []).map(edgeDB => ({
                id: `edge_${edgeDB.no_origem_id}_${edgeDB.no_destino_id}`,
                source: edgeDB.no_origem_id,
                target: edgeDB.no_destino_id,
                weight: edgeDB.peso,
                sourceName: edgeDB.nome_origem,
                targetName: edgeDB.nome_destino,
                circulacao: normalizarCirculacao(edgeDB.circulacao), // novo
              }));
                setNodes(nodesAdaptados);
                setEdges(edgesAdaptados);
            }
        } catch (err) {
            toast.error("Falha ao carregar os dados do mapa.");
        } finally {
            setIsLoading(false);
        }
    };
    carregarDadosCompletos();
}, [feiraId]);

  const obterCorDoTipoEspaco = (tipoId) => {
    const tipo = tiposEspacoFeira.find(t => t.tipo_espaco_id === Number(tipoId));
    return tipo?.cor || "#808080"; // Cor padrão
  };

  // Combina os dados de SVG e os dados cadastrais dos estandes
  const standsCompletos = useMemo(() => {
    if (!mapData?.paths || !estandesCadastrados) return [];
    
    return Object.values(mapData.paths).map(pathData => {
      const estande = estandesCadastrados.find(e => e.hash_path === pathData.hash);
      if (!estande) return null;

      return {
        ...estande,
        path: pathData.path,
        cor: obterCorDoTipoEspaco(estande.tipo_espaco_id),
        nodeId: `dest_${pathData.hash}`,
      };
    }).filter(Boolean);
  }, [mapData, estandesCadastrados, tiposEspacoFeira]);

  useEffect(() => {
    const urlOrigem = searchParams.get('origem');
    const urlDestino = searchParams.get('destino');

    // Se a URL contém os parâmetros E os dados do mapa já carregaram
    if (urlOrigem && urlDestino && nodes.length > 0 && edges.length > 0) {
      // Valida se os nós da URL realmente existem
      const origemExiste = nodes.some(n => n.id === urlOrigem);
      const destinoExiste = nodes.some(n => n.id === urlDestino);

      if (origemExiste && destinoExiste) {
        setOrigemId(urlOrigem);
        setDestinoId(urlDestino);
        
        const resultado = calcularRotaContextual(urlOrigem, urlDestino);
        setRotaCalculada(resultado);
        
        if (resultado) {
          setInstrucoes(generateDirections(resultado.path));
          toast.success("Rota carregada a partir do link!");
        }
      } else {
        toast.error("A rota do link compartilhado contém pontos inválidos.");
      }
    }
  }, [nodes, edges, searchParams]);
  
  const handleStandClick = (stand) => {
    // Verifica se o estande clicado tem um nó de destino correspondente
    const nóDoEstande = nodes.find(n => n.id === stand.nodeId);
    if (!nóDoEstande) {
      toast.warn("Este espaço não é um destino válido para roteamento.");
      return;
    }

    if (!origemId) {
      setOrigemId(stand.nodeId);
      toast.info(`Origem definida: ${stand.nome}`);
    } else if (!destinoId) {
      setDestinoId(stand.nodeId);
      toast.info(`Destino definido: ${stand.nome}`);
    } else {
      // Se ambos já estiverem definidos, reinicia a seleção pela origem
      setDestinoId('');
      setOrigemId(stand.nodeId);
      setRotaCalculada(null);
      setInstrucoes([]);
      toast.info(`Nova origem definida: ${stand.nome}. Selecione um destino.`);
    }
  };

  const handleLimparRota = () => {
    setOrigemId('');       // Limpa o seletor de origem
    setDestinoId('');      // Limpa o seletor de destino
    setRotaCalculada(null); // Remove a rota do mapa
    setInstrucoes([]);      // Remove as instruções em texto
    toast.info("Seleção de rota limpa.");
  };

  const handleCalcularRota = () => {
    if (!origemId || !destinoId) {
      toast.warn("Por favor, selecione um ponto de origem e um de destino.");
      return;
    }
    
    const resultado = calcularRotaContextual(origemId, destinoId);
    setRotaCalculada(resultado);
    
    if (resultado) {
      setInstrucoes(generateDirections(resultado.path));
      toast.success("Rota encontrada!");
    } else {
      setInstrucoes([]);
      toast.error("Não foi possível encontrar um caminho.");
    }
  };
  
  if (isLoading) return <div className="text-center p-4"><Spinner /> Carregando mapa...</div>;
  if (!mapData) return <p>Não foi possível carregar os dados do mapa.</p>;

  const destinosDisponiveis = nodes.filter(n => n.type === 'destination').sort((a,b) => a.name.localeCompare(b.name));
  const bounds = [[0, 0], [-mapData.height, mapData.width]];
  const shareUrl = rotaCalculada 
    ? `${window.location.origin}${window.location.pathname}?origem=${origemId}&destino=${destinoId}`
    : '';

  return (
    <div className="d-flex flex-column" style={{ height: 'calc(100vh - 20px)', padding: '10px' }}>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      <Card className="mb-2">
        <CardHeader className="py-2"><h5 className="mb-0">Encontre o Caminho</h5></CardHeader>
        <CardBody className="py-2">
          <div className="d-flex flex-wrap align-items-end gap-3">
            <FormGroup className="flex-grow-1">
              <Label for="select-origem"><b>Estou em:</b></Label>
              <Input id="select-origem" type="select" value={origemId} onChange={(e) => setOrigemId(e.target.value)}>
                <option value="">Selecione ou clique no mapa...</option>
                {destinosDisponiveis.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
              </Input>
            </FormGroup>
            <FormGroup className="flex-grow-1">
              <Label for="select-destino"><b>Quero ir para:</b></Label>
              <Input id="select-destino" type="select" value={destinoId} onChange={(e) => setDestinoId(e.target.value)}>
                <option value="">Selecione ou clique no mapa...</option>
                {destinosDisponiveis.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
              </Input>
            </FormGroup>
            <Button color="primary" onClick={handleCalcularRota} disabled={!origemId || !destinoId}>
              <i className="fa-solid fa-diamond-turn-right me-2"></i>
              Traçar Rota
            </Button>
            {rotaCalculada && (
              <>
                <Button color="secondary" onClick={handleLimparRota}>
                  <i className="fa-solid fa-times me-2"></i>
                  Limpar
                </Button>
                
                <Button color="success" onClick={() => setModalCompartilharVisivel(true)}>
                  <i className="fa-solid fa-share-alt me-2"></i>
                  Compartilhar
                </Button>
              </>
            )}
          </div>
        </CardBody>
      </Card>

    
      <div className="d-flex flex-grow-1" style={{ minHeight: 0, overflow: 'hidden', width: '100%' }}>
        
        {/* Área do Mapa: Agora com flex: 1 para ocupar o máximo de espaço sem forçar largura */}
        <div style={{ flex: 1, position: 'relative' }}>
          <MapContainer 
            crs={L.CRS.Simple} 
            bounds={bounds} 
            zoomSnap={0}           // Permite que o mapa pare em qualquer nível de zoom para preencher a tela
            minZoom={-5}           // Permite diminuir o zoom se necessário
            style={{ 
              height: '100%', 
              width: '100%', 
              backgroundColor: 'white' // Define o fundo como branco para não parecer "vazio"
            }}
          >
            <MapAutoFit bounds={bounds} /> {/* Chama o ajuste automático aqui */}
            <ImageOverlay url={fundoUrl} bounds={bounds} />

            <SVGOverlay attributes={{ viewBox: `0 0 ${mapData.width} ${mapData.height}` }} bounds={bounds}>
              {standsCompletos.map(stand => (
                <InteractiveStand
                  key={stand.id}
                  stand={stand}
                  onClick={handleStandClick}
                />
              ))}
            </SVGOverlay>
            
            <AntPath positions={rotaCalculada?.path.map(node => [-node.y, node.x]) || []} />
          </MapContainer>
        </div>

        {/* Lista de Instruções: Removida a altura fixa de 600px para usar a altura do pai */}
        {instrucoes.length > 0 && (
          <div style={{ width: '350px', display: 'flex' }}>
            <Card className="w-100 d-flex flex-column" style={{ height: '100%' }}>
              <CardHeader className="py-2">
                <i className="fa-solid fa-person-walking-arrow-right me-2"></i>
                Seu Caminho
              </CardHeader>
              <CardBody className="p-0" style={{ overflowY: 'auto' }}>
                <ListGroup flush>
                  {instrucoes.map((instrucao, index) => (
                    <ListGroupItem key={index} dangerouslySetInnerHTML={{ __html: instrucao }} />
                  ))}
                </ListGroup>
              </CardBody>
            </Card>
          </div>
        )}
      </div>


      <Modal isOpen={modalCompartilharVisivel} toggle={() => setModalCompartilharVisivel(false)} centered>
        <ModalHeader toggle={() => setModalCompartilharVisivel(false)}>
          Compartilhar Rota
        </ModalHeader>
        <ModalBody className="text-center">
          <p>Escaneie o QR Code com seu celular para abrir esta rota.</p>
          <div className="p-3 bg-light d-inline-block">
            <QRCodeSVG value={shareUrl} size={256} />
          </div>
          <Input 
            type="text" 
            value={shareUrl} 
            readOnly 
            className="mt-3" 
            onClick={(e) => e.target.select()} // Seleciona o texto ao clicar
          />
        </ModalBody>
      </Modal>
    </div>
  );
};

export default VisualizacaoRotaCadastro;
